import { useContext, useEffect, useState } from 'react'
import CustomPaper from '../../../containers/Paper'
import StatisticsService from '../../../services/StatisticsService'
import { StatisticsContext } from '../../../context/StatisticsContext'
import { CircularProgress } from '@mui/material'
import { Bar, Pie } from 'react-chartjs-2'
import { statisticsColors } from '../StatisticsPage'
import { formatDaysToYearsAndMonths } from '../../../helpers/utils'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import WorkOffOutlinedIcon from '@mui/icons-material/WorkOffOutlined'
import DownloadIcon from '@mui/icons-material/Download'
import CustomIconButton from '../../../components/IconButton'
import UnemployedGraduatesPDF from './UnemployedGraduatesPDF'
import { PDFDownloadLink } from '@react-pdf/renderer'

export interface UnemployedGraduate {
    firstName: string
    lastName: string
    phone: string
    email: string
    abilities: string[]
}

interface DataType {
    usersWithoutWork: number
    percentage: number
    averageDaysUnemployed: number
    usersWithoutWorkList: UnemployedGraduate[]
}

const UnemployedGraduates = ({ chart = false }: { chart?: boolean }) => {
    const { startDate, endDate, carreer } = useContext(StatisticsContext)
    const [data, setData] = useState<DataType>()

    useEffect(() => {
        new StatisticsService()
            .getUsersWithoutWork(carreer, startDate, endDate)
            .then((response) => {
                if (response.status !== 200) return
                console.log(response)
                setData(response.data)
            })
    }, [carreer, startDate, endDate])

    const getChartData = () => {
        return {
            labels: ['Con empleo', 'Desempleados'],
            datasets: [
                {
                    label: 'Cantidad de graduados',
                    data: [
                        Math.round(((data?.usersWithoutWork ?? 1) * 100) / (data?.percentage ?? 1)),
                        data?.usersWithoutWork,
                    ],
                    backgroundColor: statisticsColors.background,
                    borderColor: statisticsColors.border,
                },
            ],
        }
    }

    const getOptions = (type: 'bar' | 'pie') => ({
        maintainAspectRatio: false,
        plugins: {
            legend: { display: true },
            tooltip: {
                callbacks: {
                    label(context: any) {
                        const label: string = context.dataset.label
                        const value = context.raw
                        const percentage =
                            value === data?.usersWithoutWork
                                ? data?.percentage
                                : 100 - (data?.percentage ?? 0)
                        return type === 'pie'
                            ? `${label}: ${Number(percentage?.toFixed(2))}% (${value})`
                            : `${label}: ${value} (${Number(percentage?.toFixed(2))}%)`
                    },
                },
            },
        },
    })

    return (
        <CustomPaper className={`flex flex-col gap-2 h-full text-primary`}>
            {!chart && (
                <div className="flex gap-2 items-center -mt-2">
                    <span className="text-2xl font-semibold">Graduados desempleados</span>
                    {data && (
                        <PDFDownloadLink
                            document={<UnemployedGraduatesPDF data={data.usersWithoutWorkList} />}
                            fileName="Reporte_Graduados_Desempleados.pdf"
                        >
                            <CustomIconButton
                                icon={<DownloadIcon />}
                                className="mt-1"
                            />
                        </PDFDownloadLink>
                    )}
                </div>
            )}
            <div className="flex-1 flex">
                {data !== undefined ? (
                    !!data ? (
                        chart ? (
                            <div className="flex flex-col md:flex-row justify-around w-full">
                                <div>
                                    <Pie
                                        data={getChartData()}
                                        options={getOptions('pie')}
                                    />
                                </div>
                                <div>
                                    <Bar
                                        data={getChartData()}
                                        options={getOptions('bar')}
                                    />
                                </div>
                            </div>
                        ) : (
                            <div className="flex flex-col gap-2 md:gap-4 text-sm md:text-lg w-full">
                                <div className="flex flex-col gap-1 border-2 border-solid border-primary rounded-lg p-1">
                                    <span className="items-center flex gap-2">
                                        <WorkOffOutlinedIcon /> Cantidad de graduados desempleados:
                                    </span>
                                    <span className="font-semibold">
                                        {`${data.usersWithoutWork} graduados (${Number(data.percentage.toFixed(2))}% del total)`}
                                    </span>
                                </div>
                                <div className="flex flex-col gap-1 border-2 border-solid border-primary rounded-lg p-1">
                                    <span className="items-center flex gap-2">
                                        <AccessTimeIcon /> Tiempo promedio estando desempleados:
                                    </span>
                                    <span className="font-semibold">
                                        {formatDaysToYearsAndMonths(data.averageDaysUnemployed)}
                                    </span>
                                </div>
                            </div>
                        )
                    ) : (
                        <div>{chart ? '' : 'No hay datos'}</div>
                    )
                ) : (
                    <div className="w-full h-full flex justify-center items-center">
                        <CircularProgress
                            className="m-2 md:m-5 text-primary"
                            size={64}
                        />
                    </div>
                )}
            </div>
        </CustomPaper>
    )
}

export default UnemployedGraduates
